import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Button,
    TextField,
    InputAdornment,
    Chip,
    alpha,
    useTheme
} from '@mui/material';
import {
    Chat as ChatIcon,
    Send as SendIcon,
    Close as CloseIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    History as HistoryIcon,
    TrendingUp as TrendingUpIcon,
    SmartToy as BotIcon,
    Assignment as ReportIcon
} from '@mui/icons-material';
import ChatMessage from './ChatMessage';
import TypingIndicator from './TypingIndicator';
import MultiStepReport from './MultiStepReport';

const ChatArea = ({
    isFullscreen,
    currentChatTitle,
    multiStepData,
    handleFullscreen,
    handleToggleChat,
    chatContainerRef,
    messages,
    isLoading,
    progressInfo,
    messagesEndRef,
    inputRef,
    inputValue,
    setInputValue,
    handleKeyPress,
    handleSendMessage,
    formatTimestamp,
    onSaveToMain
}) => {
    const theme = useTheme();
    const [isReportOpen, setIsReportOpen] = useState(false);

    // Check if there's completed multi-step data to show report button
    const hasCompletedMultiStep = multiStepData?.completedSteps && multiStepData?.completedSteps === multiStepData?.totalSteps;
    // console.log('hasCompletedMultiStep: ', hasCompletedMultiStep, multiStepData)

    // Handle opening report with URL update
    const handleOpenReport = () => {
        setIsReportOpen(true);

        // Add reportId to URL if available
        if (multiStepData?.reportId) {
            const url = new URL(window.location);
            url.searchParams.set('reportid', multiStepData.reportId);
            window.history.replaceState({}, '', url);
        }
    };

    // Handle closing report with URL cleanup
    const handleCloseReport = () => {
        setIsReportOpen(false);

        // Remove reportId from URL
        const url = new URL(window.location);
        url.searchParams.delete('reportid');
        window.history.replaceState({}, '', url);
    };

    return (
        <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
        }}>
            {/* Chat header */}
            <Box
                sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    color: 'rgba(0, 0, 0, 0.87)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', width: isFullscreen ? '90%' : 'auto'}}>
                    {!isFullscreen && (
                        <ChatIcon sx={{ mr: 1, color: 'rgba(0, 0, 0, 0.87)' }} />
                    )}
                    <Typography 
                        variant={isFullscreen ? "h6" : "body1"} 
                        sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.87)', pr:2, width: isFullscreen ? 'auto' : 120 }}
                    >
                        {/* {isFullscreen ? currentChatTitle : 'Business Assistant'} */}
                        {currentChatTitle}
                    </Typography>
                    {hasCompletedMultiStep && (
                        <Button
                            size="small"
                            startIcon={<ReportIcon />}
                            onClick={handleOpenReport}
                            sx={{
                                mr: 1,
                                color: 'white',
                                bgcolor: '#1570EF',
                                '&:hover': {
                                    bgcolor: '#1d4ed8',
                                    transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease-in-out',
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 2
                            }}
                        >
                            Report
                        </Button>
                    )}
                    {multiStepData && !hasCompletedMultiStep && (
                        <Chip
                            label={`${multiStepData.completedSteps || 0}/${multiStepData.totalSteps || 0} steps`}
                            size="small"
                            color="secondary"
                            sx={{ ml: 2 }}
                            icon={<TrendingUpIcon />}
                        />
                    )}
                </Box>
                
                <Box>
                    {!isFullscreen && (
                        <Button
                            size="small"
                            startIcon={<HistoryIcon />}
                            onClick={handleFullscreen}
                            sx={{ 
                                mr: 1,
                                color: 'white',
                                bgcolor: 'rgb(79, 56, 240)',
                                '&:hover': { 
                                    bgcolor: '#1d4ed8',
                                    transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease-in-out',
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 2
                            }}
                        >
                            History
                        </Button>
                    )}

                    <IconButton 
                        size="small" 
                        onClick={handleFullscreen} 
                        sx={{ 
                            color: 'inherit',
                            p: 0.5,
                            alignSelf: 'center'
                        }}
                    >
                        {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                    </IconButton>
                    <IconButton 
                        size="small" 
                        onClick={handleToggleChat} 
                        sx={{ 
                            color: 'inherit',
                            p: 0.5,
                            alignSelf: 'center'
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* Messages area */}
            <Box
                ref={chatContainerRef}
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    bgcolor: 'background.default',
                    py: 2
                }}
            >
                {messages.length === 0 ? (
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%',
                        flexDirection: 'column',
                        gap: 2,
                        color: 'text.secondary'
                    }}>
                        <BotIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                        <Typography variant="h6" color="text.secondary">
                            How can I help you today?
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {messages.map((message, index) => (
                            <ChatMessage
                                key={message.id}
                                message={message}
                                index={index}
                                messages={messages}
                                isFullscreen={isFullscreen}
                                multiStepData={multiStepData}
                                formatTimestamp={formatTimestamp}
                                onSaveToMain={onSaveToMain}
                            />
                        ))}
                        {isLoading && (
                            <TypingIndicator
                                isLoading={isLoading}
                                progressInfo={progressInfo}
                                isFullscreen={isFullscreen}
                            />
                        )}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </Box>

            {/* Input area */}
            <Box
                sx={{
                    p: isFullscreen ? 3 : 2,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                }}
            >
                <Box sx={{ 
                    maxWidth: isFullscreen ? 800 : '100%', 
                    mx: 'auto',
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-end'
                }}>
                    <TextField
                        ref={inputRef}
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={10}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Message..."
                        disabled={isLoading}
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                bgcolor: 'background.paper',
                                '& fieldset': {
                                    borderColor: alpha(theme.palette.grey[400], 0.5)
                                },
                                '&:hover fieldset': {
                                    borderColor: 'grey.400'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main'
                                },
                                '& textarea': {
                                    resize: 'none'
                                }
                            },
                        }}
                        InputProps={{
                            endAdornment: inputValue.trim() && (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleSendMessage}
                                        disabled={!inputValue.trim() || isLoading}
                                        color="primary"
                                        sx={{
                                            p: 1,
                                            bgcolor: inputValue.trim() ? 'primary.main' : 'transparent',
                                            color: inputValue.trim() ? 'primary.contrastText' : 'text.disabled',
                                            '&:hover': {
                                                bgcolor: inputValue.trim() ? 'primary.dark' : 'transparent'
                                            },
                                            '&.Mui-disabled': {
                                                bgcolor: 'transparent',
                                                color: 'text.disabled'
                                            }
                                        }}
                                    >
                                        <SendIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
            </Box>

            {/* Multi-step Clean Report Dialog */}
            {hasCompletedMultiStep && (
                <MultiStepReport
                    open={isReportOpen}
                    onClose={handleCloseReport}
                    reportData={multiStepData}
                />
            )}
        </Box>
    );
};

export default ChatArea;